import { ssrTrack } from '@/lib/actions/analytics/ssr-track';
import { getDisplayNumber } from '@/lib/numbers';
import {
    baseUsageAtom,
    openTargetOffsetModalAtom,
    zipCodeAtom,
} from '@/ui/atoms/form-atoms';
import { useEnergyOffset } from '@/ui/hooks/useEnergyOffset';
import { useResetInputs } from '@/ui/hooks/useResetInputs';
import { useTargetOffsetLimits } from '@/ui/hooks/useTargetOffsetLimits';
import { Loader, Modal } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import styles from './TargetOffsetModal.module.css';

// Helper function to copy text to clipboard
const copyToClipboard = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        console.error('Failed to copy: ', err);
        return false;
    }
};

export const TargetOffsetModal: React.FC = () => {
    const { data: session } = useSession();
    const email = session?.user?.email;
    const [openTargetOffsetModal, setOpenTargetOffsetModal] = useAtom(
        openTargetOffsetModalAtom
    );
    const [zipCode] = useAtom(zipCodeAtom);
    const { limits, isLoading: limitsLoading } = useTargetOffsetLimits(zipCode);

    const [baseUsage] = useAtom(baseUsageAtom);
    const { inputComplete, loading, usageResult, errorMessage } =
        useEnergyOffset();

    const resetInputs = useResetInputs();

    // States to track copy success
    const [offsetCopied, setOffsetCopied] = useState(false);
    const [kwhCopied, setKwhCopied] = useState(false);

    // Calculate the target total (pre-solar usage + incremental usage)
    const preSolarUsage = Number(baseUsage) || 0;
    const incrementalUsage = usageResult?.added_to_input || 0;
    let targetTotal = preSolarUsage + incrementalUsage;

    // Calculate the offset percentage (targetTotal / preSolarUsage)
    let offsetPercentage = 100; // Default to 100% if preSolarUsage is 0
    if (preSolarUsage > 0) {
        offsetPercentage = Math.round((targetTotal / preSolarUsage) * 100);
    }

    // Apply minimum and maximum offset limits from API response
    if (limits) {
        // Apply minimum offset if available and current offset is below minimum
        if (limits.minOffset) {
            if (offsetPercentage < limits.minOffset) {
                targetTotal = (preSolarUsage * limits.minOffset) / 100;
                offsetPercentage = limits.minOffset;
            }
        }

        // Apply maximum offset if available and current offset exceeds maximum
        if (limits.maxOffset) {
            if (offsetPercentage > limits.maxOffset) {
                targetTotal = (preSolarUsage * limits.maxOffset) / 100;
                offsetPercentage = limits.maxOffset;
            }
        }
    }

    // Format the values for display
    const displayOffsetPct = `${offsetPercentage}%`;
    const displayKwh = `${getDisplayNumber(targetTotal)} kWh`;

    // Handle copying offset percentage to clipboard
    const handleCopyOffset = async () => {
        const success = await copyToClipboard(offsetPercentage.toString());
        if (success) {
            setOffsetCopied(true);
            setTimeout(() => setOffsetCopied(false), 2000);
        }
    };

    // Handle copying kWh to clipboard
    const handleCopyKwh = async () => {
        // Use targetTotal.toString() to copy the raw number without commas
        const success = await copyToClipboard(targetTotal.toString());
        if (success) {
            setKwhCopied(true);
            setTimeout(() => setKwhCopied(false), 2000);
        }
    };

    useEffect(() => {
        if (
            displayOffsetPct &&
            displayKwh &&
            email &&
            !loading &&
            !limitsLoading
        ) {
            ssrTrack('offsetModalResults', {
                offsetPct: displayOffsetPct,
                kwh: displayKwh,
                user: email,
            });
        }
    }, [displayOffsetPct, displayKwh, loading, limitsLoading, email]);

    return (
        <Modal
            opened={openTargetOffsetModal}
            onClose={() => {
                setOpenTargetOffsetModal(false);
                ssrTrack('offsetModalClickOutside');
            }}
            centered
            withCloseButton={false}
            radius={8}
            size="auto"
            className={styles.modalBody}
        >
            <div className={styles.wrapper} style={{ minWidth: '40vw' }}>
                <h2 className={styles.title}>Your Computed Results:</h2>

                {loading || limitsLoading ? (
                    <div className={styles.loadingContainer}>
                        <Loader type="dots" size={30} color="#1f2674" />
                        <p>Calculating your energy offset...</p>
                    </div>
                ) : (
                    <div className={styles.resultsContainer}>
                        <div className={styles.resultColumn}>
                            <div className={styles.resultLabel}>
                                Yearly Target Energy Offset
                            </div>
                            <div className={styles.resultValue}>
                                {displayOffsetPct}
                                <button
                                    onClick={handleCopyOffset}
                                    className={styles.copyButton}
                                    aria-label="Copy offset percentage"
                                >
                                    {offsetCopied ? (
                                        <IconCheck size={18} color="#3CB879" />
                                    ) : (
                                        <IconCopy size={18} color="#1f2674" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className={styles.resultColumn}>
                            <div className={styles.resultLabel}>Yearly kWh</div>
                            <div className={styles.resultValue}>
                                {displayKwh}
                                <button
                                    onClick={handleCopyKwh}
                                    className={styles.copyButton}
                                    aria-label="Copy kWh value"
                                >
                                    {kwhCopied ? (
                                        <IconCheck size={18} color="#3CB879" />
                                    ) : (
                                        <IconCopy size={18} color="#1f2674" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.buttons}>
                    <button
                        className={styles.restartButton}
                        onClick={() => {
                            ssrTrack('offsetModalClickRestart');
                            setOpenTargetOffsetModal(false);
                            resetInputs();
                        }}
                    >
                        Restart
                    </button>
                    <button
                        className={styles.closeButton}
                        onClick={() => {
                            ssrTrack('offsetModalClickClose');
                            setOpenTargetOffsetModal(false);
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};
